import React, { useState, useEffect } from 'react';
import { CloseOutlined, SaveOutlined, DeleteOutlined } from '@ant-design/icons';
import './Modal.css';
import type { ModalProps } from '../../types/components/modal';

const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
};

const parseDate = (dateString: string): string | null => {
    const [day, month, year] = dateString.split('.');
    if (!day || !month || !year) return null;
    const date = new Date(`${year}-${month}-${day}`);
    if (isNaN(date.getTime())) return null;
    return date.toISOString();
};

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    onSave,
    value,
    mode,
    title,
    placeholder,
    showCloseButton = true,
    subHeader
}) => {
    const [input, setInput] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (mode === 'date' && value) {
            setInput(formatDate(value));
        } else if (mode === 'text' && value) {
            setInput(value);
        } else {
            setInput('');
        }
        setError(null);
    }, [value, isOpen, mode]);

    const handleSave = () => {
        if (!input.trim()) {
            onSave(null);
            onClose();
            return;
        }
        if (mode === 'date') {
            const parsedDate = parseDate(input);
            if (!parsedDate) {
                setError('Неверный формат даты. Используйте формат ДД.ММ.ГГГГ');
                return;
            }
            onSave(parsedDate);
        } else {
            onSave(input);
        }
        onClose();
    };

    const handleClear = () => {
        onSave(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className={`modal-overlay ${isOpen ? 'visible' : ''}`}>
            <div className="modal-content">
                <div className="modal-header">
                    <div>
                    <h2 className="modal-title">{title || (mode === 'date' ? 'Введите дату' : 'Введите текст')}</h2>
                        {subHeader && <div className="modal-subheader">{subHeader}</div>}
                    </div>
                    {showCloseButton && (
                    <button className="modal-close" onClick={onClose}>
                        <CloseOutlined />
                    </button>
                    )}
                </div>
                <input
                    type="text"
                    className="date-input"
                    value={input}
                    onChange={e => {
                        if (mode === 'text' && e.target.value.length > 50) {
                            setError('Максимум 50 символов');
                            return;
                        }
                        setInput(e.target.value);
                        setError(null);
                    }}
                    placeholder={placeholder || (mode === 'date' ? 'ДД.ММ.ГГГГ' : 'Введите текст')}
                    maxLength={mode === 'text' ? 50 : undefined}
                />
                {error && <div className="error-message">{error}</div>}
                <div className="modal-actions">
                    <button className="modal-button secondary" onClick={handleClear}>
                        <DeleteOutlined className="modal-btn-icon" /> Очистить
                    </button>
                    <button className="modal-button primary" onClick={handleSave}>
                        <SaveOutlined className="modal-btn-icon" /> Сохранить
                    </button>
                </div>
            </div>
        </div>
    );
};
